/**
 * downloadPdfBlob — single source of truth for triggering a real OS-level
 * download of a PDF blob across the whole app.
 *
 * Why this exists (vs. just `pdf.save()` or anchor[download]):
 *   • Anchor[download].click() inside the Emergent preview iframe is
 *     silently blocked at the sandbox layer (no allow-downloads flag).
 *   • Chrome's built-in PDF viewer intercepts navigations to URLs ending
 *     in `.pdf` and renders inline, ignoring Content-Disposition: attachment.
 *   • Routing through /api/pdf/{id}?action=download serves an HTML wrapper
 *     whose inline auto-click script fires a real <a download> Save anchor
 *     in a top-level browser tab. Chrome can't intercept HTML, so the
 *     anchor click triggers the OS save dialog.
 *
 * Behavior:
 *   • In the deployed environment (no iframe): blob → /api/store-pdf →
 *     window.open('/api/pdf/{id}?action=download', '_blank') → file saves.
 *   • Inside the Emergent preview iframe: short-circuits with an honest
 *     toast pointing the user to "Open in new tab" (the iframe sandbox
 *     blocks downloads at the browser level — no JS workaround exists).
 *
 * Returns a promise that resolves to `{ ok, pdfId?, wrapperUrl? }`. Callers
 * usually don't need to inspect it; the toast surface handles UX.
 */

import { toast } from 'sonner';
import { isInEmergentPreview } from './sandboxDetect';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * @param {Blob} blob - The generated PDF as a Blob (e.g. jsPDF.output('blob')).
 * @param {string} filename - Suggested filename (with .pdf extension).
 * @param {object} [opts]
 * @param {'download'|'print'} [opts.mode='download']
 * @param {string} [opts.successDescription] - Override the toast description.
 */
export async function downloadPdfBlob(blob, filename, opts = {}) {
  const mode = opts.mode === 'print' ? 'print' : 'download';

  // Preview-iframe short-circuit. Telling the truth beats lying about success.
  if (isInEmergentPreview()) {
    const tabUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;
    toast.message(
      mode === 'print' ? "Print doesn't work in this preview" : "Downloads don't work in this preview",
      {
        duration: 30000,
        description:
          'Open the app in a real browser tab (or use your deployed site) — downloads and print work normally there.',
        action: {
          label: 'Open in new tab',
          onClick: () => { window.open(tabUrl, '_blank'); },
        },
      },
    );
    return { ok: false, reason: 'preview-sandbox' };
  }

  try {
    const storeResp = await fetch(`${API_URL}/api/store-pdf`, {
      method: 'POST',
      headers: { 'X-Filename': filename },
      body: blob,
    });
    if (!storeResp.ok) throw new Error(`store-pdf failed: ${storeResp.status}`);
    const { pdf_id } = await storeResp.json();
    const wrapperUrl = `${API_URL}/api/pdf/${pdf_id}?action=${mode}`;

    const win = window.open(wrapperUrl, '_blank');
    if (win && !win.closed) {
      // Use a green check + "Saved to your computer ✓" wording instead of the
      // ambiguous "Downloading…". The browser hands the file off to the OS
      // synchronously once the wrapper page's auto-click fires, so by the
      // time this toast renders the file is essentially in Downloads. 3-second
      // duration so the user sees it without feeling nagged.
      toast.success(
        mode === 'print' ? '🖨️ Opening print dialog' : `✓ Saved to your computer`,
        {
          duration: 3500,
          description:
            opts.successDescription ||
            (mode === 'print'
              ? 'Your print dialog should appear in a moment.'
              : `${filename} is in your browser's Downloads folder.`),
        },
      );
    } else {
      // Popup blocked → clickable fallback
      toast.message('Click to continue', {
        duration: 20000,
        description: `Your browser blocked the auto-${mode}. Click below to continue.`,
        action: {
          label: mode === 'print' ? 'Open print preview' : 'Download now',
          onClick: () => { window.location.href = wrapperUrl; },
        },
      });
    }
    return { ok: true, pdfId: pdf_id, wrapperUrl };
  } catch (e) {
    console.error('downloadPdfBlob failed:', e);
    toast.error(mode === 'print' ? 'Print failed' : 'Download failed', {
      description: e?.message || 'Please try again.',
    });
    return { ok: false, reason: 'error', error: e };
  }
}
